import React, { useState, useEffect } from 'react';
import { Users, Calendar, CheckCircle, XCircle, Clock, BarChart3 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DashboardStats {
  totalMeetings: number;
  confirmedMeetings: number;
  pendingMeetings: number;
  rejectedMeetings: number;
  upcomingMeetings: number;
  meetingsPerType: {
    [key: string]: number;
  };
  meetingsPerMonth: {
    [key: string]: number;
  };
}

export default function DashboardStats() {
  const [stats, setStats] = useState<DashboardStats>({
    totalMeetings: 0,
    confirmedMeetings: 0,
    pendingMeetings: 0,
    rejectedMeetings: 0,
    upcomingMeetings: 0,
    meetingsPerType: {},
    meetingsPerMonth: {},
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch all meetings
      const { data: meetings, error } = await supabase
        .from('meetings')
        .select('*');

      if (error) throw error;

      const now = new Date();
      const stats: DashboardStats = {
        totalMeetings: meetings.length,
        confirmedMeetings: meetings.filter(m => m.confirmed === true).length,
        pendingMeetings: meetings.filter(m => m.confirmed === null).length,
        rejectedMeetings: meetings.filter(m => m.confirmed === false).length,
        upcomingMeetings: meetings.filter(m => new Date(m.date) > now).length,
        meetingsPerType: {},
        meetingsPerMonth: {},
      };

      // Calculate meetings per type
      meetings.forEach(meeting => {
        const type = meeting.meeting_type === 'internal_interview' 
          ? '1. Interview (intern)'
          : meeting.meeting_type === 'client_meeting'
          ? 'Termin beim Kunden'
          : 'Sonstiges';

        stats.meetingsPerType[type] = (stats.meetingsPerType[type] || 0) + 1;
      });

      // Calculate meetings per month
      meetings.forEach(meeting => {
        const date = new Date(meeting.date);
        const monthYear = date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
        stats.meetingsPerMonth[monthYear] = (stats.meetingsPerMonth[monthYear] || 0) + 1;
      });

      setStats(stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Gesamt</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Bestätigt</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.confirmedMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ausstehend</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pendingMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Abgelehnt</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.rejectedMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Anstehend</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.upcomingMeetings}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Meetings per Type */}
        <div className="bg-white p-6 rounded-lg shadow border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-500" />
            Termine nach Art
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.meetingsPerType).map(([type, count]) => (
              <div key={type} className="flex items-center">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">{type}</span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({Math.round((count / stats.totalMeetings) * 100)}%)
                    </span>
                  </div>
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${(count / stats.totalMeetings) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="ml-4 text-sm font-medium text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Meetings per Month */}
        <div className="bg-white p-6 rounded-lg shadow border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-green-500" />
            Termine pro Monat
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.meetingsPerMonth)
              .sort((a, b) => {
                const dateA = new Date(a[0].split(' ')[1], new Date(Date.parse(a[0])).getMonth());
                const dateB = new Date(b[0].split(' ')[1], new Date(Date.parse(b[0])).getMonth());
                return dateB.getTime() - dateA.getTime();
              })
              .map(([month, count]) => (
                <div key={month} className="flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{month}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({Math.round((count / stats.totalMeetings) * 100)}%)
                      </span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${(count / stats.totalMeetings) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="ml-4 text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}